import cn from 'classnames'
import { useCallback, useRef, useState } from 'react'

import { useSafetyTranslation } from 'cng-common/features/translations'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import type { TCockpitAttentionGrouping } from '../../../../model/types'

import styles from './AttentionStatusesSearch.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.search

const PLACEHOLDER_KEYS: Record<Exclude<TCockpitAttentionGrouping, 'market'>, string> = {
  pbm: KEYS.placeholderPbm,
  manager: KEYS.placeholderManager,
  partner: KEYS.placeholderPartner,
}

interface IAttentionStatusesSearchProps {
  /** Which level is being searched — it names what the field looks through. */
  grouping: Exclude<TCockpitAttentionGrouping, 'market'>
  value: string
  onChange: (value: string) => void
}

/**
 * Narrows the grouped level to what the reader types. It rests as one of the header's chips and
 * opens into a field on hover or focus, so the control row keeps its width until it is used; a
 * field holding a query stays open, because the list below it is standing on that query.
 */
export const AttentionStatusesSearch = ({
  grouping,
  value,
  onChange,
}: IAttentionStatusesSearchProps) => {
  const t = useSafetyTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isHovered, setHovered] = useState(false)

  const label = t(KEYS.ariaLabel)

  /* Hovering the chip is the act of reaching for the field, so the caret goes there with it. */
  const handleOpen = useCallback(() => {
    setHovered(true)
    inputRef.current?.focus()
  }, [])

  const handleClear = useCallback(() => {
    onChange('')
    inputRef.current?.focus()
  }, [onChange])

  return (
    <div
      className={cn(styles.root, { [styles.filled]: value.length > 0, [styles.open]: isHovered })}
      onMouseEnter={handleOpen}
      onMouseLeave={() => setHovered(false)}
    >
      <i className={cn('kbase kbase-search', styles.icon)} aria-hidden="true" />

      <input
        ref={inputRef}
        type="search"
        className={styles.input}
        value={value}
        aria-label={label}
        placeholder={t(PLACEHOLDER_KEYS[grouping])}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            handleClear()
          }
        }}
      />

      {value.length > 0 && (
        <button
          type="button"
          className={styles.clear}
          aria-label={t(KEYS.clear)}
          onClick={handleClear}
        >
          <i className="kbase kbase-close" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

AttentionStatusesSearch.displayName = 'AttentionStatusesSearch'
